import { PlayerAssetUrls } from '@vimeo/core/AppTypes';

export enum PlayerType {
  BarebonePlayer,
  VimeoPlayer,
}

export interface PlayerAsset {
  js: string;
  css: string;
}

/**
 * Will add the player related assets to DOM.
 * This class would only add the assets if it is not already added.
 * And would remove the assets only if, it has been added by this instance.
 */
export class PlayerLoader {
  private asset: PlayerAsset = { js: '', css: '' };
  private isJSAdded = false;
  private isCSSAdded = false;
  private type: PlayerType;
  private assetUrls: PlayerAssetUrls;

  constructor(type: PlayerType, assetUrls: PlayerAssetUrls) {
    this.type = type;
    this.assetUrls = assetUrls;
  }

  /**
   * Will attach the JS and CSS to DOM if not already attached
   */
  load = () => {
    this.asset = this.getAssets();
    const promises: Promise<any>[] = [];
    if (!this.checkIfJsExists) {
      promises.push(this.addJs());
    }
    if (!this.checkIfCSSExists) {
      promises.push(this.addCss());
    }
    return Promise.all(promises.length ? promises : [Promise.resolve()]);
  };

  /**
   * Will remove the player JS and CSS from DOM, if it was attached by this instance of class.
   */
  cleanup = () => {
    if (this.isJSAdded) {
      this.removeJs();
      this.isJSAdded = false;
    }
    if (this.isCSSAdded) {
      this.removeCss();
      this.isCSSAdded = false;
    }
  };

  private get checkIfJsExists() {
    return this.asset.js == null;
  }

  private get checkIfCSSExists() {
    return this.asset.css == null;
  }

  private getAssets: () => PlayerAsset = () => {
    switch (this.type) {
      case PlayerType.VimeoPlayer:
        return {
          js: this.assetUrls.js,
          css: this.assetUrls.css,
        };
      case PlayerType.BarebonePlayer:
        return {
          js: this.assetUrls.barebone_js,
          css: this.assetUrls.css,
        };
      default:
        throw new TypeError(
          'Invalid PlayerType, must be either .BarebonePlayer or .ChromelessPlayer or .VimeoPlayer!',
        );
    }
  };

  private addJs = () => {
    const element: HTMLScriptElement = document.createElement('script');
    element.src = this.asset.js;
    document.body.appendChild(element);
    const promise = this.generatePromise(element);
    promise.then(() => (this.isJSAdded = true));
    return promise;
  };

  private addCss = () => {
    const element: HTMLLinkElement = document.createElement('link');
    element.rel = 'stylesheet';
    element.type = 'text/css';
    element.media = 'screen';
    element.href = this.asset.css;
    document.getElementsByTagName('head')[0].appendChild(element);
    const promise = this.generatePromise(element);
    promise.then(() => (this.isCSSAdded = true));
    return promise;
  };

  private removeJs = () => {
    const script = document.querySelector(`script[src="${this.asset.js}"]`);
    if (script && script.parentNode) {
      script.parentNode.removeChild(script);
    }
  };

  private removeCss = () => {
    const link = document.querySelector(`link[href="${this.asset.css}"]`);
    if (link && link.parentNode) {
      link.parentNode.removeChild(link);
    }
  };

  private generatePromise = (element: HTMLScriptElement | HTMLLinkElement) => {
    return new Promise((resolve, reject) => {
      element.onload = resolve;
      element.onabort = reject;
      element.onerror = reject;
    });
  };
}
